/**
 * The words the Inquiries destination says, and the shapes it says them
 * in.
 *
 * The API sends the vocabulary — CONTEXT.md's own Phone Call source, the
 * Inquiry's status and failure tag — and this file is where each becomes
 * English. It is shared by the list and the screen one arrival opens on,
 * so a call cannot read as "Answered" in one place and "answered_call" in
 * the other.
 *
 * A value this build does not know reads as the plain thing it is
 * ("Phone call", "Something went wrong") rather than as its machine word.
 * That is the same rule the New Inquiry email follows, for the same
 * reason: a tag is a name the code chose, and a Company never agreed to
 * read it.
 */

import badge from "../components/ui/badge.module.css";

/**
 * How it arrived, as CONTEXT.md's **Provenance** plus the form.
 *
 * The form is in the same vocabulary because from the Company's side
 * there is one question — how did this reach us — and a call and a form
 * submission are two answers to it, not two subjects.
 */
export function arrivedLabel(arrived: string): string {
  switch (arrived) {
    case "answered_call":
      return "Answered";
    case "voicemail":
      return "Voicemail";
    case "receptionist":
      return "Receptionist";
    case "missed_call":
      return "Missed";
    case "form":
      return "Form";
    default:
      return "Phone call";
  }
}

/**
 * The badge each arrival wears, in the RMS's own phase colours so the two
 * lists read as one product: yellow is the one still waiting on somebody
 * (a voicemail is one-sided until it is rung back), green is the one a
 * person took, purple is the crew — here, the Receptionist — and blue is
 * the one that came in typed. Red is the one nobody took at all: a call
 * on the Company's own number that rang out with no voicemail and no
 * Receptionist, which is a number to ring back (#207). A value this build
 * does not know is gray.
 *
 * The colours themselves are in `components/ui/badge.module.css`; this map
 * is only which one each arrival wears.
 */
const ARRIVED_VARIANTS: Record<string, string> = {
  answered_call: badge.answeredCall,
  voicemail: badge.voicemail,
  receptionist: badge.receptionist,
  missed_call: badge.missedCall,
  form: badge.form,
};

/**
 * The status-badge shape from DESIGN.md, coloured for how it arrived.
 *
 * It returns a class string rather than a component because two surfaces
 * draw this badge — the Inquiries list and the job's thread — and what they
 * share is the look, not a wrapper element.
 */
export function arrivedBadgeClass(arrived: string): string {
  return `${badge.badge} ${ARRIVED_VARIANTS[arrived] ?? badge.unknown}`;
}

/** The filter's options. Closed here and closed again in Go. */
export const ARRIVED_OPTIONS = [
  { value: "", label: "All Arrivals" },
  { value: "answered_call", label: "Answered" },
  { value: "voicemail", label: "Voicemail" },
  { value: "receptionist", label: "Receptionist" },
  { value: "missed_call", label: "Missed" },
  { value: "form", label: "Form" },
];

/**
 * What it became. Three answers, and "nothing" is the ordinary one: most
 * calls to a published number are a supplier, a Contractor about their
 * own pay, or a wrong number.
 */
export const BECAME_OPTIONS = [
  { value: "", label: "All Outcomes" },
  { value: "job", label: "Became a job" },
  { value: "failed", label: "Failed to draft" },
  { value: "nothing", label: "Became nothing" },
];

/**
 * Why a draft failed, in the Company's terms rather than the tag's.
 *
 * Every one of these is a thing that happened to their job, and half of
 * them are a thing at their own rental system — so the sentence says
 * which end it was, because that is what decides whether there is
 * anything for them to do.
 */
const FAILURE_LABELS: Record<string, string> = {
  gemini_transport: "The quote draft did not come back",
  gemini_parse: "The quote draft came back unreadable",
  gemini_timeout: "The quote draft took too long",
  booking_insert: "Your rental system would not take the job",
  picker: "The gear could not be chosen",
  form_data: "The answers could not be read",
  profile_resolve: "We could not tell which form it was for",
  db: "We could not save it",
  dry_hire_equipment: "Something asked for is not in your catalogue",
  role_catalogue_empty: "Your rental system listed no gear to quote from",
  backend_unavailable: "Your rental system did not answer",
  connection_owner: "Your rental system needs an owner on new jobs",
  unknown: "Something went wrong",
};

export function failureLabel(reason: string): string {
  return FAILURE_LABELS[reason] ?? "Something went wrong";
}

/**
 * Why a call was judged not a hire enquiry. The **Discard Reason**'s own
 * closed set, said the way somebody would say it out loud.
 */
const DISCARD_LABELS: Record<string, string> = {
  receptionist_not_enquiry: "Not a hire enquiry",
  operator_dismissed: "Dismissed by your team",
  supplier_or_sales: "A supplier or a sales call",
  contractor_own_job: "A contractor about their own job",
  existing_booking_admin: "Admin on a job already booked",
  message: "A message",
  wrong_number: "A wrong number",
  personal: "A personal call",
  no_content: "Nothing was said",
};

export function discardLabel(reason: string): string {
  return DISCARD_LABELS[reason] ?? "Not a hire enquiry";
}

/**
 * New Zealand time, because the Company reading this answered the phone
 * in it. The timestamp arrives in UTC and is formatted here rather than
 * in Go: the API sends the vocabulary, the screen says it.
 */
const WHEN = new Intl.DateTimeFormat("en-NZ", {
  timeZone: "Pacific/Auckland",
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
});

/** The same instant with its year, for a screen showing one arrival. */
const WHEN_FULL = new Intl.DateTimeFormat("en-NZ", {
  timeZone: "Pacific/Auckland",
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function formatWhen(at: string | null): string {
  return format(WHEN, at);
}

export function formatWhenFull(at: string | null): string {
  return format(WHEN_FULL, at);
}

function format(fmt: Intl.DateTimeFormat, at: string | null): string {
  if (!at) return "";
  const when = new Date(at);
  return Number.isNaN(when.getTime()) ? "" : fmt.format(when);
}

/**
 * The day a job is on, off the Inquiry's YYYY-MM-DD, as a person
 * would write it on a whiteboard: "14 Mar 2026". Read as date parts and
 * never through the viewer's zone, which is the shared nz-date rule
 * (a date-only string parsed as UTC midnight is yesterday west of
 * Greenwich). "" for no date, or one that will not parse.
 */
const EVENT_DAY = new Intl.DateTimeFormat("en-NZ", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

export function formatEventDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(day.getTime()) ? "" : EVENT_DAY.format(day);
}

/**
 * How many calls a job has had and when the last one rang, as one line
 * under the job (#115): "5 calls · last 8 Sept, 2:15 pm". Nothing for a
 * job of one call, whose one call is the row, and nothing for a row
 * about no job.
 */
export function callsLine(calls: number, lastAt: string | null): string {
  if (calls < 2) return "";
  const last = formatWhen(lastAt);
  return last ? `${calls} calls · last ${last}` : `${calls} calls`;
}

/** Who rang, or who filled the form in. A number is a name of sorts. */
export function whoLabel(who: string, fromE164: string): string {
  return who || fromE164 || "Number withheld";
}

/**
 * Who rang and where from, as one line: "Hailey, Mellons Bay School".
 * The organisation stands alone where the person gave only that, and
 * the number stands in where they gave neither.
 */
export function whoLine(who: string, whoOrg: string, fromE164: string): string {
  if (who && whoOrg) return `${who}, ${whoOrg}`;
  return whoLabel(who || whoOrg, fromE164);
}


/**
 * What became of one arrival and what to do about it, as the notes'
 * single Outcome row.
 *
 * One row on every arrival, whatever happened: a job drafted, a
 * follow-up on one, a failure, a wait, or nothing to do. It said what
 * the arrival became and stopped there, and the Customer's contact
 * preference sat in a row of its own beside it — "Get back to them: by
 * email", on every quick enquiry, which is always email — so the one
 * thing a reader acts on was split across two rows and neither said to
 * act. The preference is the second half of the sentence now.
 *
 * The kinds are what the row must *draw* differently and nothing else.
 * A failure is the one outcome that is about something going wrong, so
 * it is coloured. The job's link is the heading's, and is not drawn
 * again here.
 */
export type Outcome = { kind: "failed" | "plain"; text: string };

export function outcomeOf(arrival: OutcomeFacts): Outcome {
  const getBack = getBackTo(arrival.get_back_to);
  const message = messageLine(arrival);
  // An enquiry the Receptionist took is open until somebody sends it
  // (migration 071): every call and text from the number is written onto
  // it, and nothing is in the rental system yet. The buttons under the
  // notes are what moves it on.
  if (arrival.inquiry_status === "open") {
    return {
      kind: "plain",
      text: `Enquiry open. Not in ${arrival.rental_system || "your rental system"} until you send it. ${message}${getBack}`,
    };
  }
  // Sent and drafting (migration 072): the list keeps it while the
  // worker writes the job, so the reader who pressed Send finds it here.
  if (
    arrival.inquiry_status === "pending_quote" ||
    arrival.inquiry_status === "processing"
  ) {
    return {
      kind: "plain",
      text: `Sent. The job is being drafted in ${arrival.rental_system || "your rental system"}. ${message}${getBack}`,
    };
  }
  if (arrival.inquiry_status === "not_enquiry") {
    return { kind: "plain", text: "Closed as not an enquiry. Nothing was sent." };
  }
  if (arrival.inquiry_status === "quote_failed") {
    return {
      kind: "failed",
      text: `${failureLabel(arrival.failure_reason)}. ${getBack}`,
    };
  }
  // A call that said nothing about a job: a lead to ring back, with no
  // job anywhere, because every line on one would have been invented.
  if (arrival.inquiry_status === "nothing_to_draft") {
    return {
      kind: "plain",
      text: `Nothing drafted: they didn't say what the job is. ${getBack}`,
    };
  }
  // A Company that does not want intake to quote (#273): the enquiry is
  // written up and nothing drafted, by its choice, so the next step is
  // theirs.
  if (arrival.inquiry_status === "written_up") {
    return { kind: "plain", text: `Written up for you to quote. ${getBack}` };
  }
  if (arrival.became_inquiry) {
    return {
      kind: "plain",
      text: `Job drafted in ${arrival.rental_system || "your rental system"}. ${message}${getBack}`,
    };
  }
  if (arrival.kind === "call") {
    // A message about the job is what the reader has to do, so it is
    // what the row says; any changes are on the job either way.
    if (arrival.status === "proposed") {
      return {
        kind: "plain",
        text: message
          ? `A follow-up on a job. ${message}Get back to them.`
          : "A follow-up on a job. The changes they asked for are on it, waiting to be accepted.",
      };
    }
    if (arrival.status === "failed") {
      return {
        kind: "failed",
        text: `The call could not be written up. ${getBack}`,
      };
    }
    // Nobody spoke: a number to ring back and nothing else to read.
    if (arrival.arrived === "missed_call") {
      return { kind: "plain", text: "Missed call. Ring them back." };
    }
  }
  // A message asks something of the reader however the call was judged,
  // so it says so rather than "Nothing to do".
  if (arrival.discard_reason === "message" || message) {
    return {
      kind: "plain",
      text: `${message || "They left a message. "}Get back to them.`,
    };
  }
  if (arrival.discard_reason) {
    return {
      kind: "plain",
      text: `Nothing to do: ${lowerFirst(discardLabel(arrival.discard_reason))}.`,
    };
  }
  return { kind: "plain", text: "Nothing to do." };
}

/**
 * The half of the Outcome a reader acts on: how the Customer asked to be
 * got back to (CONTEXT.md's **Contact Preference**), or plainly to get
 * back to them where they said nothing.
 */
/**
 * The message a caller left, as the start of a sentence with its own
 * trailing space, or "" where they left none. It rides on whatever the
 * call became (migration 047), so every Outcome that can carry one reads
 * it from here.
 */
function messageLine(arrival: OutcomeFacts): string {
  if (!arrival.left_message && !arrival.message_for) return "";
  const who = (arrival.message_for ?? "").trim();
  return who ? `They left a message for ${who}. ` : "They left a message. ";
}

function getBackTo(preference: string | undefined): string {
  const how = (preference ?? "").trim();
  return how ? `Get back to them ${how}.` : "Get back to them.";
}

/**
 * What the Outcome is read off. A form submission carries no call
 * status and nothing to discard, so those two are the call's alone.
 * `get_back_to` is the Inquiry's, and empty on an arrival that became
 * none.
 */
export type OutcomeFacts = {
  kind: "call" | "form";
  inquiry_status: string;
  failure_reason: string;
  became_inquiry: boolean;
  rental_system: string;
  get_back_to?: string;
  status?: string;
  /** How the call arrived (CONTEXT.md's **Phone Call** source): a call's alone. */
  arrived?: string;
  discard_reason?: string;
  /** Whether the caller left a message, and who for: a call's alone. */
  left_message?: boolean;
  message_for?: string;
};

/**
 * The reason, lower-cased where it follows the dash. Only the first
 * letter: a name the Company would capitalise anywhere else is one it
 * would capitalise here.
 */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
